import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Printer, X } from 'lucide-react';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { PageHead, Card, money, Fade, fmtDateTime } from './os2ui';

/* Reports — executive reports generated from real data, printable to PDF. */

const REPORTS = [
  { kind: 'weekly', en: 'Weekly Executive Report', es: 'Reporte Ejecutivo Semanal', dEn: 'Revenue, funnel, activity and priorities of the last 7 days.', dEs: 'Ingresos, funnel, actividad y prioridades de los últimos 7 días.', ready: true },
  { kind: 'leads', en: 'Lead Report', es: 'Reporte de Leads', dEn: 'Every lead with stage, score, source and next action.', dEs: 'Cada lead con etapa, score, fuente y próxima acción.', ready: true },
  { kind: 'affiliates', en: 'Affiliate Report', es: 'Reporte de Afiliados', dEn: 'Partner ranking, commissions and recruitment funnel.', dEs: 'Ranking de partners, comisiones y funnel de captación.', ready: true },
  { kind: 'revenue', en: 'Monthly Revenue Report', es: 'Reporte Mensual de Ingresos', dEn: 'Revenue by month & product, active subscriptions and affiliate payouts — real Stripe data.', dEs: 'Ingresos por mes y producto, suscripciones activas y pagos a afiliados — datos reales de Stripe.', ready: true },
  { kind: 'seo', en: 'SEO Report', es: 'Reporte SEO', dEn: 'Requires GA4 / Search Console reporting API.', dEs: 'Requiere la API de informes de GA4 / Search Console.', ready: false },
  { kind: 'investor', en: 'Investor Report', es: 'Reporte para Inversores', dEn: 'Requires expenses + 6 months of history.', dEs: 'Requiere gastos + 6 meses de historial.', ready: false },
  { kind: 'restaurant', en: 'Restaurant Report', es: 'Reporte por Restaurante', dEn: 'Per-client performance — activates with active clients.', dEs: 'Rendimiento por cliente — se activa con clientes activos.', ready: false },
];

function PrintView({ kind, data, isEs, onClose }) {
  const title = REPORTS.find((r) => r.kind === kind);
  useEffect(() => {
    document.body.classList.add('os2-print-report');
    return () => document.body.classList.remove('os2-print-report');
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-50 bg-gray-900/30 backdrop-blur-[2px] flex justify-center overflow-y-auto py-8 print:p-0 print:bg-white" data-testid="report-print-view">
      <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl p-10 print:shadow-none print:rounded-none h-fit">
        <div className="flex items-start justify-between mb-6 print:hidden">
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-[#F77F11] text-white text-[13px] font-semibold px-4 py-2 hover:bg-[#d8651a]" data-testid="report-print-btn">
            <Printer className="w-4 h-4" /> {isEs ? 'Descargar PDF' : 'Download PDF'}
          </button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="report-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-3 pb-4 mb-6 border-b-2 border-[#F77F11]">
          <img src="/kitxens-logo.png" alt="Kitxens" className="w-10 h-10 rounded-lg" />
          <div className="flex-1">
            <p className="text-[17px] font-semibold tracking-tight leading-none">kit<span className="text-[#F77F11]">x</span>ens</p>
            <p className="text-[9.5px] tracking-[0.22em] uppercase text-gray-400 font-bold mt-1">Restaurant Operating Intelligence</p>
          </div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#F77F11]">AI OS · {isEs ? 'Reporte oficial' : 'Official report'}</p>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{isEs ? title.es : title.en}</h1>
        <p className="text-[12px] text-gray-400 mb-6">{data.by} · {fmtDateTime(data.generated_at)}</p>

        {kind === 'weekly' && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[[isEs ? 'Ingresos 30d' : 'Revenue 30d', money(data.revenue.last_30d)], ['MRR', money(data.revenue.mrr)],
                [isEs ? 'Leads 7d' : 'Leads 7d', data.leads_7d], [isEs ? 'Scans 7d' : 'Scans 7d', data.scans_7d],
                [isEs ? 'Eventos 7d' : 'Events 7d', data.events_7d], [isEs ? 'Clientes' : 'Customers', data.funnel.CUSTOMER]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-gray-200 p-3">
                  <p className="text-[10.5px] uppercase tracking-wider text-gray-400 font-medium">{l}</p>
                  <p className="text-xl font-semibold tabular-nums">{v}</p>
                </div>
              ))}
            </div>
            <h2 className="text-[14px] font-semibold mb-2">{isEs ? 'Prioridades' : 'Priorities'}</h2>
            {(data.priorities || []).map((p, i) => <p key={i} className="text-[13px] text-gray-700 py-0.5">{i + 1}. {p}</p>)}
            <h2 className="text-[14px] font-semibold mt-5 mb-2">{isEs ? 'Actividad destacada' : 'Highlighted activity'}</h2>
            {(data.notifications || []).map((n, i) => (
              <p key={i} className="text-[12.5px] text-gray-600 py-0.5">{n.icon} {isEs ? n.title_es : n.title_en}{n.detail ? ` · ${n.detail}` : ''}</p>
            ))}
          </>
        )}
        {kind === 'leads' && (
          <>
            <p className="text-[13px] text-gray-600 mb-4">{data.total} {isEs ? 'contactos' : 'contacts'} · {Object.entries(data.funnel).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
            <table className="w-full text-[11.5px]">
              <thead><tr className="text-left text-gray-400 uppercase tracking-wider text-[10px]">
                <th className="py-1.5 pr-2">{isEs ? 'Contacto' : 'Contact'}</th><th className="pr-2">{isEs ? 'Etapa' : 'Stage'}</th>
                <th className="pr-2">Index AI</th><th className="pr-2">{isEs ? 'Fuente' : 'Source'}</th><th>{isEs ? 'Próxima acción' : 'Next action'}</th>
              </tr></thead>
              <tbody>
                {data.leads.map((l) => (
                  <tr key={l.email} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2"><b>{l.restaurant || l.owner || l.email}</b><br /><span className="text-gray-400">{l.email}</span></td>
                    <td className="pr-2">{l.stage} ({l.probability}%)</td>
                    <td className="pr-2 tabular-nums">{l.index_ai_score ?? '—'}</td>
                    <td className="pr-2">{l.source || '—'}{l.affiliate_name ? ` · ${l.affiliate_name}` : ''}</td>
                    <td>{l.next_action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {kind === 'affiliates' && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[[isEs ? 'Partners' : 'Partners', data.totals.affiliates], [isEs ? 'Clics' : 'Clicks', data.totals.clicks_period],
                [isEs ? 'Referidos' : 'Referrals', data.totals.referrals], [isEs ? 'Pendiente' : 'Pending', money(data.totals.pending_commissions)],
                [isEs ? 'Aprobado' : 'Approved', money(data.totals.approved_commissions)], [isEs ? 'Pagado' : 'Paid', money(data.totals.paid_commissions)]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-gray-200 p-3">
                  <p className="text-[10.5px] uppercase tracking-wider text-gray-400 font-medium">{l}</p>
                  <p className="text-xl font-semibold tabular-nums">{v}</p>
                </div>
              ))}
            </div>
            <table className="w-full text-[12px]">
              <thead><tr className="text-left text-gray-400 uppercase tracking-wider text-[10px]">
                <th className="py-1.5 pr-2">Partner</th><th className="pr-2">{isEs ? 'Clics' : 'Clicks'}</th>
                <th className="pr-2">{isEs ? 'Referidos' : 'Referrals'}</th><th>{isEs ? 'Comisiones' : 'Commissions'}</th>
              </tr></thead>
              <tbody>
                {data.ranking.map((a) => (
                  <tr key={a.ref_code} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2"><b>{a.name}</b> · {a.ref_code}</td>
                    <td className="pr-2 tabular-nums">{a.clicks}</td>
                    <td className="pr-2 tabular-nums">{a.referrals}</td>
                    <td className="tabular-nums">{money(a.commissions.pending + a.commissions.approved + a.commissions.paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {kind === 'revenue' && (
          <>
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[[isEs ? 'Ingresos totales' : 'Total revenue', money(data.revenue.total)], ['MRR', money(data.revenue.mrr)],
                ['ARR', money(data.revenue.arr)], [isEs ? 'Suscripciones activas' : 'Active subscriptions', data.subscriptions.active]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-gray-200 p-3">
                  <p className="text-[10.5px] uppercase tracking-wider text-gray-400 font-medium">{l}</p>
                  <p className="text-xl font-semibold tabular-nums">{v}</p>
                </div>
              ))}
            </div>
            <h2 className="text-[14px] font-semibold mb-2">{isEs ? 'Ingresos por mes' : 'Revenue by month'}</h2>
            <table className="w-full text-[12px] mb-5">
              <thead><tr className="text-left text-gray-400 uppercase tracking-wider text-[10px]">
                <th className="py-1.5 pr-2">{isEs ? 'Mes' : 'Month'}</th><th>{isEs ? 'Ingresos' : 'Revenue'}</th>
              </tr></thead>
              <tbody>
                {Object.entries(data.revenue.by_month || {}).map(([m, v]) => (
                  <tr key={m} className="border-t border-gray-100"><td className="py-1.5 pr-2 font-medium">{m}</td><td className="tabular-nums">{money(v)}</td></tr>
                ))}
              </tbody>
            </table>
            <h2 className="text-[14px] font-semibold mb-2">{isEs ? 'Por producto' : 'By product'}</h2>
            <div className="flex flex-wrap gap-2 mb-5">
              {Object.entries(data.revenue.by_product || {}).map(([p, v]) => (
                <span key={p} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-orange-200 bg-[#FFF7ED] text-[12px] text-[#b45309] capitalize">{p} <b className="tabular-nums">{money(v)}</b></span>
              ))}
            </div>
            <h2 className="text-[14px] font-semibold mb-2">{isEs ? 'Últimas transacciones' : 'Latest transactions'}</h2>
            <table className="w-full text-[11.5px] mb-5">
              <thead><tr className="text-left text-gray-400 uppercase tracking-wider text-[10px]">
                <th className="py-1.5 pr-2">{isEs ? 'Cliente' : 'Customer'}</th><th className="pr-2">{isEs ? 'Producto' : 'Product'}</th>
                <th className="pr-2">{isEs ? 'Importe' : 'Amount'}</th><th>{isEs ? 'Fecha' : 'Date'}</th>
              </tr></thead>
              <tbody>
                {(data.transactions || []).map((t, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2"><b>{t.name || t.email}</b></td>
                    <td className="pr-2 capitalize">{t.product}</td>
                    <td className="pr-2 tabular-nums">{money(t.amount)}</td>
                    <td className="text-gray-400">{fmtDateTime(t.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[12px] text-gray-500">
              {isEs ? 'Comisiones de afiliados' : 'Affiliate payouts'}: {isEs ? 'pendiente' : 'pending'} {money(data.affiliate_payouts.pending)} · {isEs ? 'aprobado' : 'approved'} {money(data.affiliate_payouts.approved)} · {isEs ? 'pagado' : 'paid'} {money(data.affiliate_payouts.paid)}
              <span className="text-gray-400"> · Stripe: {data.stripe.mode}</span>
            </p>
          </>
        )}
        <div className="mt-8 pt-4 border-t-2 border-[#F77F11] flex items-center justify-between text-[10.5px] text-gray-400">
          <span><b className="text-gray-600">kit<span className="text-[#F77F11]">x</span>ens</b> — Restaurant Operating Intelligence</span>
          <span>kitxens.com · {isEs ? 'Datos 100% reales, generado automáticamente' : '100% real data, generated automatically'}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ReportsOsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState('');

  const open = async (kind) => {
    setBusy(kind);
    try {
      const r = await authFetch(`/os2/report/${kind}`);
      const data = await r.json();
      setView({ kind, data });
    } finally {
      setBusy('');
    }
  };

  return (
    <div data-testid="os2-reports">
      <PageHead
        title={isEs ? 'Reportes' : 'Reports'}
        question={isEs ? '¿Qué pasó esta semana, este mes, este trimestre? — informes listos para PDF.' : 'What happened this week, this month, this quarter? — PDF-ready reports.'}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r, i) => (
          <Fade key={r.kind} delay={i * 0.05}>
            <Card className={`p-5 h-full flex flex-col ${r.ready ? 'hover:border-[#F77F11]/50 hover:shadow-md transition-all duration-300' : 'border-dashed'}`} data-testid={`report-card-${r.kind}`}>
              <FileText className={`w-6 h-6 mb-3 ${r.ready ? 'text-[#F77F11]' : 'text-gray-300'}`} strokeWidth={1.6} />
              <h2 className={`text-[14.5px] font-semibold tracking-tight ${r.ready ? 'text-gray-900' : 'text-gray-400'}`}>{isEs ? r.es : r.en}</h2>
              <p className="mt-1 text-[12.5px] text-gray-400 leading-relaxed flex-1">{isEs ? r.dEs : r.dEn}</p>
              {r.ready ? (
                <button onClick={() => open(r.kind)} disabled={busy === r.kind}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors disabled:opacity-50"
                  data-testid={`report-generate-${r.kind}`}>
                  {busy === r.kind ? '…' : (isEs ? 'Generar reporte' : 'Generate report')}
                </button>
              ) : (
                <p className="mt-4 text-[11px] text-gray-400">{isEs ? '⏳ Fuente de datos no conectada aún' : '⏳ Data source not connected yet'}</p>
              )}
            </Card>
          </Fade>
        ))}
      </div>
      {view && <PrintView kind={view.kind} data={view.data} isEs={isEs} onClose={() => setView(null)} />}
    </div>
  );
}
